import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { publicConfigError, supabase } from '../lib/supabaseClient';
import { fetchJson } from '../lib/apiClient';
import { useAuth } from './AuthContext';
import { cleanTitle } from '../utils/contentFormatting';
import {
    acknowledgePendingProgress,
    cacheWatchProgress,
    clearStoredProgress,
    createProgressTimestamp,
    getCachedProgress,
    getCachedProgressItems,
    getPendingProgress,
    pickFreshestProgress,
    queuePendingProgress,
    readPendingProgressQueue
} from '../utils/watchProgressStore';

import { AVATARS } from '../data/constants';

const ProfileContext = createContext({});
const LEGACY_PROFILE_STORAGE_KEY = 'luma_current_profile';
const WATCH_COMPLETED_THRESHOLD = 0.90;
const WATCH_COMPLETED_REMAINING_SECONDS = 3 * 60;
const WATCH_RESUME_MIN_SECONDS = 60;
const WATCH_HISTORY_CACHE_TTL = 30 * 1000;

const getProfileStorageKey = (userId) => `luma_current_profile:${userId}`;

const getStoredProfileId = (userId) => {
    if (!userId) return null;
    return localStorage.getItem(getProfileStorageKey(userId)) || localStorage.getItem(LEGACY_PROFILE_STORAGE_KEY);
};

const persistProfileId = (userId, profileId) => {
    if (!userId || !profileId) return;
    localStorage.setItem(getProfileStorageKey(userId), profileId);
    localStorage.setItem(LEGACY_PROFILE_STORAGE_KEY, profileId);
};

const clearStoredProfileId = (userId) => {
    if (userId) {
        localStorage.removeItem(getProfileStorageKey(userId));
    }
    localStorage.removeItem(LEGACY_PROFILE_STORAGE_KEY);
};

const resolveContentId = (content) => {
    if (!content) return null;
    if (content.type === 'series_episode' || content.isSeriesEpisode) {
        return String(content.episodeId || content.episode_id || content.id || content.stream_id || content.num || '');
    }
    return String(content.id || content.stream_id || content.series_id || content.num || '');
};

const isPlaybackCompleted = (progress, duration, explicitCompleted = false) => {
    if (explicitCompleted) return true;
    if (!duration || duration <= 0) return false;
    if (progress >= duration) return true;
    const watchedRatio = progress / duration;
    const remainingSeconds = duration - progress;
    return watchedRatio >= WATCH_COMPLETED_THRESHOLD || remainingSeconds <= WATCH_COMPLETED_REMAINING_SECONDS;
};

const buildWatchProgressPayload = (profileId, content, progressSeconds, durationSeconds, categoryName = null, options = {}) => {
    const contentId = resolveContentId(content);
    if (!profileId || !contentId) return null;

    const duration = Math.max(0, Math.floor(Number(durationSeconds) || 0));
    const rawProgress = Math.max(0, Math.floor(Number(progressSeconds) || 0));
    const progress = duration > 0 ? Math.min(rawProgress, duration) : rawProgress;
    const isEpisode = content.type === 'series_episode' || content.isSeriesEpisode;
    const completed = isPlaybackCompleted(progress, duration, Boolean(options.completed));
    const episodeTitle = cleanTitle(content.episodeTitle || content.episode_title || (isEpisode ? content.title : null));
    const seriesTitle = cleanTitle(content.seriesTitle || content.series_title || content.seriesName || content.name || content.title);

    const payload = {
        profile_id: profileId,
        content_id: contentId,
        content_title: isEpisode
            ? (episodeTitle || cleanTitle(content.title || content.name))
            : cleanTitle(content.title || content.name),
        content_type: isEpisode ? 'series_episode' : (content.type || 'movie'),
        poster_url: content.poster_url || content.poster || content.cover || content.image || content.stream_icon || content.info?.movie_image,
        stream_url: content.url || content.stream_url || null,
        progress_seconds: completed && duration > 0 ? duration : progress,
        duration_seconds: duration,
        completed,
        last_watched_at: createProgressTimestamp()
    };

    if (categoryName) payload.category_name = categoryName;

    if (isEpisode) {
        payload.series_id = String(content.seriesId || content.series_id || content.parentId || content.series_id_original || '');
        payload.series_title = seriesTitle || null;
        payload.season_number = Number(content.seasonNumber || content.season_number || content.season || 0) || null;
        payload.episode_number = Number(content.episodeNumber || content.episode_number || content.episode_num || 0) || null;
        payload.episode_id = contentId;
        payload.episode_title = episodeTitle || payload.content_title;
    }

    return payload;
};


export const ProfileProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [profiles, setProfiles] = useState([]);
    const [currentProfile, setCurrentProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [loadedUserId, setLoadedUserId] = useState(null);
    const flushingProgressRef = useRef(false);
    const watchHistoryCacheRef = useRef({ profileId: null, fetchedAt: 0, items: null });

    const invalidateWatchHistoryCache = () => {
        watchHistoryCacheRef.current = { profileId: null, fetchedAt: 0, items: null };
    };

    // AbortController ref for cancelling pending queries
    const abortRef = useRef(null);

    // Load profiles when user changes
    useEffect(() => {
        // Cancel any previous pending query
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }

        if (authLoading) {
            setLoading(true);
            return;
        }

        if (!user) {
            console.log('[Profiles] No user, resetting state');
            invalidateWatchHistoryCache();
            setProfiles([]);
            setCurrentProfile(null);
            setLoadedUserId(null);
            setLoading(false);
            return;
        }

        if (!supabase) {
            invalidateWatchHistoryCache();
            setProfiles([]);
            setCurrentProfile(null);
            setLoadedUserId(null);
            setError(new Error(publicConfigError));
            setLoading(false);
            return;
        }

        // Reset state immediately for new session
        console.log('[Profiles] New user detected, clearing state before fetch:', user.id);
        invalidateWatchHistoryCache();
        setProfiles([]);
        setCurrentProfile(null);

        console.log('[Profiles] loadProfiles started for user:', user.id);
        setLoading(true);
        setLoadedUserId(null);
        setError(null);

        const controller = new AbortController();
        abortRef.current = controller;

        const fetchProfiles = async () => {
            try {
                console.log('[Profiles] Fetching profiles...');
                const result = await Promise.race([
                    supabase
                        .from('profiles')
                        .select('*')
                        .eq('user_id', user.id)
                        .order('created_at', { ascending: true })
                        .abortSignal(controller.signal),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Profile query timed out (10s). Check Railway/PostgreSQL connectivity.')), 10000)
                    )
                ]);

                if (controller.signal.aborted) return;

                const { data, error: queryError } = result;
                console.log('[Profiles] Fetch completed. Data:', data?.length, 'profiles. Error:', queryError);

                if (queryError) throw queryError;

                setProfiles(data || []);

                if (data && data.length > 0) {
                    const storedProfileId = getStoredProfileId(user.id);
                    if (storedProfileId) {
                        const found = data.find(p => p.id === storedProfileId);
                        if (found) {
                            setCurrentProfile(found);
                            persistProfileId(user.id, found.id);
                        } else {
                            clearStoredProfileId(user.id);
                        }
                    }
                } else {
                    clearStoredProfileId(user.id);
                }
            } catch (err) {
                if (controller.signal.aborted) return;
                console.error('[Profiles] Error loading profiles:', err);
                setError(err);
            } finally {
                if (!controller.signal.aborted) {
                    console.log('[Profiles] setLoading(false)');
                    setLoadedUserId(user.id);
                    setLoading(false);
                }
            }
        };

        fetchProfiles();

        return () => {
            controller.abort();
        };
    }, [authLoading, user?.id, reloadKey]); // Only re-run when auth or user ID changes

    const effectiveLoading = authLoading || loading || (!!user && loadedUserId !== user.id);

    // Create a new profile
    const createProfile = async (name, avatarUrl = AVATARS[0], isKids = false, preferences = []) => {
        if (!user) return { error: 'Not authenticated' };
        if (!supabase) return { error: publicConfigError };

        try {
            const { data, error } = await supabase
                .from('profiles')
                .insert([{
                    user_id: user.id,
                    email: user.email,
                    name,
                    avatar_url: avatarUrl,
                    is_kids: isKids,
                    preferences
                }])
                .select()
                .single();

            if (error) throw error;
            setProfiles(prev => [...prev, data]);
            return { data };
        } catch (err) {
            console.error('[Profiles] Error creating profile:', err);
            return { error: err.message };
        }
    };

    // Update a profile
    const updateProfile = async (profileId, updates) => {
        if (!supabase) return { error: publicConfigError };
        try {
            const { data, error } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', profileId)
                .select()
                .single();

            if (error) throw error;
            setProfiles(prev => prev.map(p => p.id === profileId ? data : p));
            if (currentProfile?.id === profileId) {
                setCurrentProfile(data);
            }
            return { data };
        } catch (err) {
            console.error('[Profiles] Error updating profile:', err);
            return { error: err.message };
        }
    };

    // Delete a profile
    const deleteProfile = async (profileId) => {
        if (!supabase) return { error: publicConfigError };
        try {
            const { error } = await supabase
                .from('profiles')
                .delete()
                .eq('id', profileId);

            if (error) throw error;
            setProfiles(prev => prev.filter(p => p.id !== profileId));
            if (currentProfile?.id === profileId) {
                setCurrentProfile(null);
                clearStoredProfileId(user?.id);
            }
            return { success: true };
        } catch (err) {
            console.error('[Profiles] Error deleting profile:', err);
            return { error: err.message };
        }
    };

    // Select a profile
    const selectProfile = (profile) => {
        invalidateWatchHistoryCache();
        setCurrentProfile(profile);
        persistProfileId(user?.id, profile.id);
    };

    // Clear profile selection (back to profile picker)
    const clearProfileSelection = () => {
        invalidateWatchHistoryCache();
        setCurrentProfile(null);
        setGenreScores({}); // Clear scores
        clearStoredProfileId(user?.id);
    };

    // ============================================
    // GENRE SCORING & MATCHING ENGINE
    // ============================================
    const [genreScores, setGenreScores] = useState({});

    // Refresh scores based on preferences and history
    const refreshGenreScores = async () => {
        if (!currentProfile || !supabase) return;

        try {
            const scores = {};
            const basePreferences = currentProfile.preferences || [];
            const history = await getRecentlyWatched(50);

            // 1. Base scores from preferences (High initial weight)
            basePreferences.forEach(genre => {
                scores[genre.toUpperCase()] = 25; // Base preference weight
            });

            // 2. Add scores from watch history with recency decay
            history.forEach((item, index) => {
                const recencyWeight = Math.max(0.5, 3 - (index * 0.1));
                const genre = item.category_name?.toUpperCase();
                if (genre) {
                    scores[genre] = (scores[genre] || 0) + recencyWeight;
                }
            });

            console.log('[Recommendations] Updated Genre Scores:', scores);
            setGenreScores(scores);
        } catch (err) {
            console.error('[Recommendations] Error updating scores:', err);
        }
    };

    // Auto-refresh scores when profile or history triggers might change
    useEffect(() => {
        if (currentProfile) {
            refreshGenreScores();
        }
    }, [currentProfile?.id]);

    // Main matching function
    const calculateMatchScore = (content) => {
        if (!content) return 0;

        // Base match (70-85% for random content, 90-99% for matched)
        const id = Number(content.id || content.stream_id || content.num || 0);
        let baseScore = 75 + (id % 11); // Deterministic base between 75-86

        const catName = content.categoryTitle?.toUpperCase();
        const genreWeight = catName ? (genreScores[catName] || 0) : 0;

        // Apply profile weight
        if (genreWeight > 0) {
            // content matches profile interests
            baseScore += Math.min(13, genreWeight / 2); // Max +13 bonus
        } else {
            // negative weight for things not preferred? 
            // no, just keep it at neutral base
        }

        // Quality bonus (IMDb rating impact)
        const rating = parseFloat(content.rating || 0);
        if (rating > 8) baseScore += 2;
        else if (rating < 5 && rating > 0) baseScore -= 5;

        // Clamp between 65 and 99
        return Math.floor(Math.min(99, Math.max(65, baseScore)));
    };

    // ========== Watch History Operations ==========

    const getWatchHistorySnapshot = async (force = false) => {
        if (!currentProfile || !supabase) return [];

        const cached = watchHistoryCacheRef.current;
        if (
            !force &&
            cached.profileId === currentProfile.id &&
            cached.items &&
            Date.now() - cached.fetchedAt < WATCH_HISTORY_CACHE_TTL
        ) {
            return cached.items;
        }

        const { data, error } = await supabase
            .from('watch_history')
            .select('*')
            .eq('profile_id', currentProfile.id)
            .order('last_watched_at', { ascending: false });

        const localItems = readPendingProgressQueue()
            .filter(item => item.profileId === currentProfile.id && item.payload?.content_id);
        const offlineCacheItems = error ? getCachedProgressItems(currentProfile.id) : [];
        if (error && !localItems.length && !offlineCacheItems.length) throw error;

        const mergedByContentId = new Map(
            (data || []).map(item => [String(item.content_id), item])
        );
        localItems.forEach(item => {
            const contentId = String(item.payload.content_id);
            mergedByContentId.set(
                contentId,
                pickFreshestProgress(mergedByContentId.get(contentId), item.payload)
            );
        });
        offlineCacheItems.forEach(item => {
            const contentId = String(item.content_id);
            mergedByContentId.set(
                contentId,
                pickFreshestProgress(mergedByContentId.get(contentId), item)
            );
        });
        const items = Array.from(mergedByContentId.values()).sort(
            (a, b) => Date.parse(b.last_watched_at || '') - Date.parse(a.last_watched_at || '')
        );
        watchHistoryCacheRef.current = {
            profileId: currentProfile.id,
            fetchedAt: Date.now(),
            items
        };
        return items;
    };

    // Save watch progress
    const flushPendingWatchProgress = async () => {
        if (!currentProfile || !supabase || flushingProgressRef.current) return;

        const profileId = currentProfile.id;
        const queue = readPendingProgressQueue();
        const ownItems = queue.filter(item => item.profileId === profileId);
        if (!ownItems.length) return;

        flushingProgressRef.current = true;
        try {
            for (const item of ownItems) {
                const contentId = item.payload?.content_id;
                if (!contentId) continue;
                const latestItem = getPendingProgress(profileId, contentId);
                if (!latestItem) continue;

                const { error } = await supabase
                    .from('watch_history')
                    .upsert(latestItem.payload, {
                        onConflict: 'profile_id,content_id'
                    });

                if (!error) {
                    acknowledgePendingProgress(
                        profileId,
                        contentId,
                        latestItem.payload.last_watched_at
                    );
                }
            }
            invalidateWatchHistoryCache();
        } catch (err) {
            console.error('[WatchHistory] Error flushing pending progress:', err);
        } finally {
            flushingProgressRef.current = false;
        }
    };

    useEffect(() => {
        if (currentProfile) {
            flushPendingWatchProgress();
        }
    }, [currentProfile?.id]);

    useEffect(() => {
        if (!currentProfile) return undefined;

        const flushWhenAvailable = () => {
            if (document.visibilityState === 'visible' && navigator.onLine !== false) {
                flushPendingWatchProgress();
            }
        };

        window.addEventListener('online', flushWhenAvailable);
        window.addEventListener('pageshow', flushWhenAvailable);
        document.addEventListener('visibilitychange', flushWhenAvailable);
        return () => {
            window.removeEventListener('online', flushWhenAvailable);
            window.removeEventListener('pageshow', flushWhenAvailable);
            document.removeEventListener('visibilitychange', flushWhenAvailable);
        };
    }, [currentProfile?.id]);

    // Save watch progress
    const saveWatchProgress = async (content, progressSeconds, durationSeconds, categoryName = null, options = {}) => {
        if (!currentProfile || !supabase) return;

        const updateData = buildWatchProgressPayload(
            currentProfile.id,
            content,
            progressSeconds,
            durationSeconds,
            categoryName,
            options
        );

        if (!updateData) return;
        const cachedHistory = watchHistoryCacheRef.current.profileId === currentProfile.id
            ? watchHistoryCacheRef.current.items || []
            : [];
        const existingItem = pickFreshestProgress(
            cachedHistory.find(item => String(item.content_id) === String(updateData.content_id)),
            getCachedProgress(currentProfile.id, updateData.content_id),
            getPendingProgress(currentProfile.id, updateData.content_id)?.payload
        );
        updateData.last_watched_at = createProgressTimestamp(existingItem?.last_watched_at);
        updateData.duration_seconds = Math.max(
            Number(existingItem?.duration_seconds) || 0,
            Number(updateData.duration_seconds) || 0
        );
        const normalizedProgress = updateData.duration_seconds > 0
            ? Math.min(Math.max(0, Math.floor(Number(progressSeconds) || 0)), updateData.duration_seconds)
            : Math.max(0, Math.floor(Number(progressSeconds) || 0));
        updateData.completed = isPlaybackCompleted(
            normalizedProgress,
            updateData.duration_seconds,
            Boolean(options.completed)
        );
        updateData.progress_seconds = updateData.completed && updateData.duration_seconds > 0
            ? updateData.duration_seconds
            : normalizedProgress;
        if (
            existingItem?.completed &&
            !updateData.completed &&
            !options.allowUncomplete
        ) {
            updateData.completed = true;
            updateData.progress_seconds = updateData.duration_seconds > 0
                ? updateData.duration_seconds
                : Math.max(Number(existingItem.progress_seconds) || 0, Number(updateData.progress_seconds) || 0);
            updateData.duration_seconds = Math.max(
                Number(existingItem.duration_seconds) || 0,
                Number(updateData.duration_seconds) || 0
            );
        }

        // Persist locally before any network work. A crash, refresh or offline
        // transition can therefore never lose the latest known position.
        queuePendingProgress(currentProfile.id, updateData);
        invalidateWatchHistoryCache();

        if (options.queueOnly) {
            return { queued: true };
        }

        try {
            const { error } = await supabase
                .from('watch_history')
                .upsert(updateData, {
                    onConflict: 'profile_id,content_id'
                });

            if (error) throw error;
            acknowledgePendingProgress(
                currentProfile.id,
                updateData.content_id,
                updateData.last_watched_at
            );
            invalidateWatchHistoryCache();

            // If significantly watched, refresh scores to reflect new taste
            if (updateData.completed) {
                refreshGenreScores();
            }
            return { success: true };
        } catch (err) {
            console.error('[WatchHistory] Error saving progress:', err);
            return { queued: true, error: err };
        }
    };

    // Get watch progress for specific content
    const getWatchProgress = async (contentId) => {
        if (!currentProfile || !supabase) return null;

        const pending = getPendingProgress(currentProfile.id, String(contentId));
        const cached = getCachedProgress(currentProfile.id, String(contentId));
        if (navigator.onLine === false) {
            return pickFreshestProgress(cached, pending?.payload);
        }
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 4000);
        try {
            const { data, error } = await supabase
                .from('watch_history')
                .select('*')
                .eq('profile_id', currentProfile.id)
                .eq('content_id', String(contentId))
                .maybeSingle()
                .abortSignal(controller.signal);

            if (error) throw error;
            if (!data && !pending) {
                clearStoredProgress(currentProfile.id, String(contentId));
                return null;
            }
            const freshest = pickFreshestProgress(data, cached, pending?.payload);
            if (freshest) cacheWatchProgress(currentProfile.id, freshest);
            return freshest;
        } catch (err) {
            console.error('[WatchHistory] Error getting progress:', err);
            return pickFreshestProgress(cached, pending?.payload);
        } finally {
            window.clearTimeout(timeout);
        }
    };

    // Get continue watching list
    const getContinueWatching = async (limit = 20) => {
        if (!currentProfile || !supabase) return [];

        try {
            const items = await getWatchHistorySnapshot();
            return items
                .filter(item => {
                    if (item.completed || Number(item.progress_seconds) <= WATCH_RESUME_MIN_SECONDS) return false;
                    const duration = Number(item.duration_seconds) || 0;
                    if (duration <= 0) return true;
                    return !isPlaybackCompleted(Number(item.progress_seconds) || 0, duration);
                })
                .slice(0, limit);
        } catch (err) {
            console.error('[WatchHistory] Error getting continue watching:', err);
            return [];
        }
    };

    const getWatchedItems = async ({ completed, type, limit = 20 } = {}) => {
        if (!currentProfile || !supabase) return [];

        try {
            let items = await getWatchHistorySnapshot();

            if (typeof completed === 'boolean') {
                items = items.filter(item => Boolean(item.completed) === completed);
            }

            if (type) {
                const allowedTypes = Array.isArray(type) ? type : [type];
                items = items.filter(item => allowedTypes.includes(item.content_type));
            }

            return items.slice(0, limit);
        } catch (err) {
            console.error('[WatchHistory] Error getting watched items:', err);
            return [];
        }
    };

    const getSeriesProgress = async (seriesId) => {
        if (!currentProfile || !supabase || !seriesId) {
            return { items: [], completedCount: 0, inProgress: null, lastWatched: null };
        }

        try {
            const { data, error } = await supabase
                .from('watch_history')
                .select('*')
                .eq('profile_id', currentProfile.id)
                .eq('series_id', String(seriesId))
                .order('last_watched_at', { ascending: false });

            const localItems = readPendingProgressQueue()
                .filter(item => (
                    item.profileId === currentProfile.id
                    && String(item.payload?.series_id || '') === String(seriesId)
                ));
            const offlineCacheItems = error
                ? getCachedProgressItems(currentProfile.id).filter(
                    item => String(item.series_id || '') === String(seriesId)
                )
                : [];
            if (error && !localItems.length && !offlineCacheItems.length) throw error;

            const mergedByContentId = new Map(
                (data || []).map(item => [String(item.content_id), item])
            );
            localItems.forEach(item => {
                const contentId = String(item.payload.content_id);
                mergedByContentId.set(
                    contentId,
                    pickFreshestProgress(mergedByContentId.get(contentId), item.payload)
                );
            });
            offlineCacheItems.forEach(item => {
                const contentId = String(item.content_id);
                mergedByContentId.set(
                    contentId,
                    pickFreshestProgress(mergedByContentId.get(contentId), item)
                );
            });
            const items = Array.from(mergedByContentId.values()).sort(
                (a, b) => Date.parse(b.last_watched_at || '') - Date.parse(a.last_watched_at || '')
            );
            return {
                items,
                completedCount: items.filter(item => item.completed).length,
                inProgress: items.find(item => {
                    if (item.completed || Number(item.progress_seconds) <= WATCH_RESUME_MIN_SECONDS) return false;
                    const duration = Number(item.duration_seconds) || 0;
                    return duration <= 0 || !isPlaybackCompleted(Number(item.progress_seconds) || 0, duration);
                }) || null,
                lastWatched: items[0] || null
            };
        } catch (err) {
            console.error('[WatchHistory] Error getting series progress:', err);
            return { items: [], completedCount: 0, inProgress: null, lastWatched: null };
        }
    };

    const getEpisodeProgressMap = async (seriesId) => {
        const seriesProgress = await getSeriesProgress(seriesId);
        return seriesProgress.items.reduce((acc, item) => {
            const id = String(item.episode_id || item.content_id);
            const duration = Number(item.duration_seconds) || 0;
            const seconds = Number(item.progress_seconds) || 0;
            acc[id] = {
                ...item,
                progressPercent: item.completed ? 100 : (duration > 0 ? Math.min(100, Math.max(0, (seconds / duration) * 100)) : 0)
            };
            return acc;
        }, {});
    };

    // Get recently watched (completed items)
    const getRecentlyWatched = async (limit = 20) => {
        if (!currentProfile || !supabase) return [];

        try {
            const items = await getWatchHistorySnapshot();
            return items.slice(0, limit);
        } catch (err) {
            console.error('[WatchHistory] Error getting recently watched:', err);
            return [];
        }
    };

    // Get all watch progress entries for current profile (for catalog progress badges)
    const getAllWatchProgress = async () => {
        if (!currentProfile || !supabase) return [];

        try {
            return await getWatchHistorySnapshot();
        } catch (err) {
            console.error('[WatchHistory] Error getting full progress list:', err);
            return [];
        }
    };

    // Remove a single item from history ("Reprendre" / Continue Watching)
    const removeFromHistory = async (contentId) => {
        if (!currentProfile || !supabase || !contentId) return { success: false };
        try {
            const { error } = await supabase
                .from('watch_history')
                .delete()
                .eq('profile_id', currentProfile.id)
                .eq('content_id', String(contentId));
            if (error) throw error;
            clearStoredProgress(currentProfile.id, String(contentId));
            invalidateWatchHistoryCache();
            return { success: true };
        } catch (err) {
            console.error('[WatchHistory] Error removing item:', err);
            return { success: false, error: err };
        }
    };

    // Mark a movie/episode as watched (completed) or reset it to unwatched
    const setContentWatched = async (content, watched, durationSeconds = 0) => {
        if (!currentProfile || !supabase) return { success: false };
        const contentId = resolveContentId(content);
        if (!contentId) return { success: false };
        try {
            if (watched) {
                const dur = Math.max(1, Math.floor(Number(durationSeconds) || 0));
                await saveWatchProgress(content, dur, dur, null, { completed: true });
            } else {
                await removeFromHistory(contentId);
            }
            return { success: true };
        } catch (err) {
            console.error('[WatchHistory] Error setting watched state:', err);
            return { success: false, error: err };
        }
    };

    // ============================================
    // RECOMMENDATION ENGINE
    // ============================================
    // Server-side content-based engine (uses real genres from the catalog DB),
    // with a graceful fallback to the legacy client-side heuristic.
    const getRecommendations = async (allContent, limit = 24) => {
        if (!currentProfile) return [];

        try {
            const token = localStorage.getItem('luma_access_token');
            const data = await fetchJson(
                `/api/recommendations?profileId=${encodeURIComponent(currentProfile.id)}&limit=${limit}`,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            if (data?.recommendations?.length) {
                return data.recommendations;
            }
        } catch (err) {
            console.warn('[Recommendations] Server engine unavailable, using fallback:', err.message);
        }

        return getRecommendationsLegacy(allContent, limit);
    };

    const getRecommendationsLegacy = async (allContent, limit = 20) => {
        if (!currentProfile || !allContent || allContent.length === 0) return [];

        try {
            // 1. Get profile preferences (from onboarding)
            const basePreferences = currentProfile.preferences || [];

            // 2. Get watch history (last 50 items) to analyze taste
            const history = await getRecentlyWatched(50);
            const watchedIds = new Set(history.map(h => h.content_id));

            // 3. Build genre scores
            const genreScores = {};

            // Base score from preferences (High initial weight - significantly increased to preserve stability)
            basePreferences.forEach(genre => {
                genreScores[genre] = 20;
            });

            // Add scores from watch history with recency decay
            // TUNED: Reduced weight of recent items to prevent "jumping" too fast
            history.forEach((item, index) => {
                // Decay factor: recent items have weight, but less aggressive
                // Item 0 (most recent) gets +2 (was +5), Item 49 gets ~0.5
                const recencyWeight = Math.max(0.5, 2 - (index * 0.05));

                const genre = item.category_name;
                if (genre) {
                    // Normalize genre string if needed (e.g. "Action & Adventure" -> "Action")
                    // For now assuming exact match with category titles
                    genreScores[genre] = (genreScores[genre] || 0) + recencyWeight;
                }
            });

            // If no history and no preferences, return popular/interesting content (fallback)
            if (Object.keys(genreScores).length === 0) {
                // Fallback: Return movies from popular or high-quality categories
                const fallbackKeywords = ['top', 'popular', '4k', 'uhd', 'nouveau', 'tendance', 'recent', 'canal+'];
                let candidates = allContent
                    .filter(cat => cat.type !== 'live' && fallbackKeywords.some(kw => cat.title.toLowerCase().includes(kw)))
                    .flatMap(cat => cat.movies || []);

                // If still empty (e.g. no matching categories), take a mix from the first few categories
                if (candidates.length === 0) {
                    candidates = allContent.slice(0, 5).flatMap(cat => cat.movies || []);
                }

                return candidates.slice(0, limit);
            }

            // 4. Rank genres
            const topGenres = Object.entries(genreScores)
                .sort(([, scoreA], [, scoreB]) => scoreB - scoreA) // Descending
                .map(([genre]) => genre);

            // 5. Gather candidates from top genres
            let recommendations = [];
            const seenIds = new Set();

            // Iterate through top genres and pick content
            for (const genre of topGenres) {
                // Find category matching the genre
                // Flexible matching: "Action" matches "Films Action", "Action & Aventure", etc.
                const categories = allContent.filter(cat =>
                    cat.type !== 'live' && cat.title.toLowerCase().includes(genre.toLowerCase())
                );

                for (const cat of categories) {
                    if (!cat.movies) continue;

                    for (const movie of cat.movies) {
                        const id = String(movie.id || movie.stream_id);

                        // Exclude already watched and duplicates
                        if (!watchedIds.has(id) && !seenIds.has(id)) {
                            recommendations.push({
                                ...movie,
                                score: genreScores[genre] // Assign score for sorting
                            });
                            seenIds.add(id);
                        }
                    }
                }
            }

            // 6. Shuffle/Sort and Limit
            // We randomize slightly within top scores to keep it fresh
            return recommendations
                .sort((a, b) => b.score - a.score + (Math.random() * 2 - 1))
                .slice(0, limit);

        } catch (err) {
            console.error('[Recommendations] Error generating recommendations:', err);
            return [];
        }
    };


    // Clear watch history for current profile
    const clearWatchHistory = async () => {
        if (!currentProfile || !supabase) return;

        try {
            const { error } = await supabase
                .from('watch_history')
                .delete()
                .eq('profile_id', currentProfile.id);

            if (error) throw error;
        } catch (err) {
            console.error('[WatchHistory] Error clearing history:', err);
        }
    };

    // ========== My List Operations ==========
    const [myListIds, setMyListIds] = useState(new Set());
    const [myListItemsCache, setMyListItemsCache] = useState([]);
    const [myListLoadedProfileId, setMyListLoadedProfileId] = useState(null);

    // Load my list IDs when profile changes
    useEffect(() => {
        const loadMyListIds = async () => {
            if (!currentProfile || !supabase) {
                setMyListIds(new Set());
                setMyListItemsCache([]);
                setMyListLoadedProfileId(null);
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('my_list')
                    .select('*')
                    .eq('profile_id', currentProfile.id)
                    .order('added_at', { ascending: false });

                if (error) throw error;
                setMyListIds(new Set(data?.map(item => item.content_id) || []));
                setMyListItemsCache(data || []);
                setMyListLoadedProfileId(currentProfile.id);
            } catch (err) {
                console.error('[MyList] Error loading list IDs:', err);
            }
        };

        loadMyListIds();
    }, [currentProfile, reloadKey]);

    // Add to my list
    const addToMyList = async (content) => {
        if (!currentProfile) return { error: 'No profile selected' };
        if (!supabase) return { error: publicConfigError };

        const contentId = String(content.id || content.stream_id || content.series_id || content.num);

        try {
            const { error } = await supabase
                .from('my_list')
                .insert({
                    profile_id: currentProfile.id,
                    content_id: contentId,
                    content_title: content.title || content.name,
                    content_type: content.type || (content.series_id ? 'series' : 'movie'),
                    poster_url: content.cover || content.stream_icon || content.image,
                    stream_url: content.url
                });

            if (error) {
                if (error.code === '23505') {
                    // Already exists, not an error
                    return { success: true };
                }
                throw error;
            }

            setMyListIds(prev => new Set([...prev, contentId]));
            setMyListItemsCache(prev => [{
                id: `optimistic:${contentId}`,
                profile_id: currentProfile.id,
                content_id: contentId,
                content_title: content.title || content.name,
                content_type: content.type || (content.series_id ? 'series' : 'movie'),
                poster_url: content.cover || content.stream_icon || content.image,
                stream_url: content.url,
                added_at: new Date().toISOString()
            }, ...prev.filter(item => String(item.content_id) !== contentId)]);
            return { success: true };
        } catch (err) {
            console.error('[MyList] Error adding to list:', err);
            return { error: err.message };
        }
    };

    // Remove from my list
    const removeFromMyList = async (contentId) => {
        if (!currentProfile) return { error: 'No profile selected' };
        if (!supabase) return { error: publicConfigError };

        const id = String(contentId);

        try {
            const { error } = await supabase
                .from('my_list')
                .delete()
                .eq('profile_id', currentProfile.id)
                .eq('content_id', id);

            if (error) throw error;

            setMyListIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setMyListItemsCache(prev => prev.filter(item => String(item.content_id) !== id));
            return { success: true };
        } catch (err) {
            console.error('[MyList] Error removing from list:', err);
            return { error: err.message };
        }
    };

    // Get my list items
    const getMyList = async () => {
        if (!currentProfile || !supabase) return [];
        if (myListLoadedProfileId === currentProfile.id) return myListItemsCache;

        try {
            const { data, error } = await supabase
                .from('my_list')
                .select('*')
                .eq('profile_id', currentProfile.id)
                .order('added_at', { ascending: false });

            if (error) throw error;
            const items = data || [];
            setMyListItemsCache(items);
            setMyListLoadedProfileId(currentProfile.id);
            setMyListIds(new Set(items.map(item => item.content_id)));
            return items;
        } catch (err) {
            console.error('[MyList] Error getting list:', err);
            return [];
        }
    };

    // Check if item is in list (synchronous using cached IDs)
    const isInMyList = (contentId) => {
        return myListIds.has(String(contentId));
    };

    // Toggle item in list
    const toggleMyList = async (content) => {
        const contentId = String(content.id || content.stream_id || content.series_id || content.num);
        if (isInMyList(contentId)) {
            return await removeFromMyList(contentId);
        } else {
            return await addToMyList(content);
        }
    };

    const value = {
        profiles,
        currentProfile,
        // Use effectiveLoading which accounts for stale data
        loading: effectiveLoading,
        error: error?.message || error || null,
        hasProfiles: profiles.length > 0,
        createProfile,
        updateProfile,
        deleteProfile,
        selectProfile,
        clearProfileSelection,
        loadProfiles: () => { },
        reloadProfiles: () => setReloadKey((previous) => previous + 1),
        saveWatchProgress,
        getWatchProgress,
        getContinueWatching,
        getRecentlyWatched,
        getAllWatchProgress,
        getWatchedItems,
        getSeriesProgress,
        getEpisodeProgressMap,
        removeFromHistory,
        setContentWatched,
        flushPendingWatchProgress,
        clearWatchHistory,
        getRecommendations, // New recommendation function
        // My List
        addToMyList,
        removeFromMyList,
        getMyList,
        isInMyList,
        toggleMyList,
        calculateMatchScore,
        refreshGenreScores,
        AVATARS
    };

    return (
        <ProfileContext.Provider value={value}>
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => useContext(ProfileContext);
